import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Download, History, Home } from 'lucide-react';
import { formatCents, formatDate, type HistoryLease } from '@vuokramani/shared';
import { PageHeader } from '../../components/page.tsx';
import { EmptyState, Panel, Splash } from '../../components/ui.tsx';
import { leaseExportUrl, useTenantHistory } from '../../lib/history.ts';
import { contractUrl, depositPdfUrl } from '../../lib/tenancy.ts';
import l from '../shared/logs.module.css';

/**
 * Vuokrahistoria: päättyneet vuokrasuhteet säilytysajan verran. Jokaisesta voi avata lokin
 * ja ladata koko aineiston zipinä (loki, laskut, maksut, tarkastusten kuvat).
 */
export function HistoryPage() {
  const { t } = useTranslation();
  const { data: leases, isLoading } = useTenantHistory();
  if (isLoading) return <Splash />;

  return (
    <>
      <PageHeader title={t('nav.history')} />
      {!leases || leases.length === 0 ? (
        <EmptyState
          icon={<History size={22} />}
          title={t('tenant.historyEmptyTitle')}
          text={t('tenant.historyEmptyText')}
        />
      ) : (
        <>
          <p className={l.note}>{t('tenant.historyNote')}</p>
          <Panel>
            {leases.map((lease) => (
              <HistoryRow key={lease.leaseId} lease={lease} />
            ))}
          </Panel>
        </>
      )}
    </>
  );
}

function HistoryRow({ lease }: { lease: HistoryLease }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const period = `${formatDate(lease.startDate, lang)} – ${
    lease.endDate ? formatDate(lease.endDate, lang) : t('invoices.untilFurther')
  }`;
  const meta = [
    lease.address,
    period,
    lease.depositReturnedOn
      ? t('tenant.historyDeposit', {
          amount: formatCents(lease.depositReturnedCents ?? 0, lang),
          date: formatDate(lease.depositReturnedOn, lang),
        })
      : null,
    lease.accessUntil ? t('log.accessUntil', { date: formatDate(lease.accessUntil, lang) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={l.row}>
      <Home size={18} aria-hidden="true" className={l.icon} />
      <div className={l.text}>
        <span className={l.titleStrong}>{lease.propertyTitle}</span>
        <span className={l.meta}>{meta}</span>
      </div>
      <div className={l.actions}>
        <Link to={`/vuokralainen/loki/${lease.leaseId}`}>{t('log.title')}</Link>
        {lease.hasContract && (
          <a href={contractUrl(lease.leaseId)} download>
            {t('tenant.historyContract')}
          </a>
        )}
        {lease.depositReturnedOn && (
          <a href={depositPdfUrl(lease.leaseId)} download>
            {t('deposit.pdfShort')}
          </a>
        )}
        <a href={leaseExportUrl(lease.leaseId)} download>
          <Download size={14} aria-hidden="true" />
          {t('tenant.historyDownload')}
        </a>
      </div>
    </div>
  );
}
